'use client';

import type { ReactNode } from 'react';
import Image from 'next/image';
import { useReducedMotion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useBurnStats } from '@/hooks/use-burn-stats';

/**
 * Centrepiece of `/burn`. Four-layer composition (back to front):
 *
 *   1. `FireVideoBackdrop` — the looping fire video, pinned to the
 *      bottom 30% of the section as the back-most layer.
 *   2. `MascotBackdrop` — the boss-lobster character grounded on top
 *      of the video, glaring upward through the rest of the stack.
 *   3. `AtmosphereOverlay` — a uniform black darken plus bottom red
 *      gradients that lift the foreground text out of the busy scene.
 *   4. Foreground text content (`relative z-10`) — either the live burn
 *      count + supply share (once burning is unlocked) or the pre-launch
 *      "burn unlocks in…" countdown + progress bar, rendered last so it
 *      always reads cleanly.
 *
 * The hero is intentionally lean: no wallet-connect, no "Burn now"
 * CTA — those land in a follow-up once the contract surface exposes a
 * burn function.
 */
export function BurnHero() {
  const query = useBurnStats();

  // Until the treasury accumulates enough $TAKEOVER to hit the cap,
  // burning is locked and we show the "burn unlocks in…" progress bar.
  // Default to the locked view while the query is in flight — pre-launch
  // (locked) is the expected state, so the progress bar is the right
  // skeleton to fall back to rather than flashing the burn counter.
  const unlocked = query.data?.unlock.unlocked ?? false;

  return (
    <section
      aria-labelledby={unlocked ? 'burn-headline' : 'burn-unlock-headline'}
      className="relative isolate flex min-h-[calc(100vh-2.5rem)] flex-col overflow-hidden bg-black"
    >
      <FireVideoBackdrop />
      <MascotBackdrop />
      <AtmosphereOverlay />

      <div className="relative z-10 flex flex-1 items-center justify-center px-6 py-20 md:py-28 md:pt-20">
        <div className="flex w-full max-w-4xl flex-col items-center gap-6 text-center">
          {unlocked ? (
            <>
              <BurnHeadline query={query} />
              <PercentOfSupply query={query} />
            </>
          ) : (
            <BurnUnlockProgress query={query} />
          )}
        </div>
      </div>
    </section>
  );
}

/**
 * Looping fire video filling the entire hero as the back-most layer.
 * Held at 30% opacity so it reads as an ambient firelight wash rather
 * than dominating the scene — the mascot + overlay still sit clearly
 * on top. Autoplays muted (the only way modern browsers allow autoplay)
 * and loops indefinitely. Users with `prefers-reduced-motion` get a
 * subtle static red wash — also saves the ~3 MB download for visitors
 * who explicitly opted out of motion.
 */
function FireVideoBackdrop() {
  const reduced = !!useReducedMotion();

  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 select-none"
    >
      {reduced ? (
        <div className="h-full w-full bg-[linear-gradient(to_top,rgba(255,61,61,0.20),transparent_65%)]" />
      ) : (
        <video
          src="/burn/fire.mp4"
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          className="h-full w-full object-cover opacity-30"
        />
      )}
    </div>
  );
}

/**
 * Boss-lobster character. The wrapper extends 30% below the section's
 * bottom edge — the section's `overflow-hidden` clips that excess, so
 * `object-bottom` ends up anchoring the image off-screen and the legs
 * get cropped while the head, chest, and raised claws stay visible
 * to frame the headline.
 */
function MascotBackdrop() {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-x-0 top-0 select-none"
      style={{ bottom: '-30%' }}
    >
      <Image
        src="/burn/background.png"
        alt=""
        fill
        priority
        sizes="100vw"
        className="object-contain object-bottom opacity-90"
      />
    </div>
  );
}

/**
 * Solid black darken + a softened bottom red gradient. Sits between
 * the video/mascot layers and the foreground text so the headline
 * reads clearly while the flames still hint at heat along the bottom.
 */
function AtmosphereOverlay() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0">
      {/* Uniform darken — pushes the character + fire back enough that
          the foreground type doesn't have to fight them for contrast. */}
      <div className="absolute inset-0 bg-black/75" />
      {/* Subtle ember-bloom along the bottom — kept faint so the
          fire video itself can carry most of the warm-light load. */}
      <div className="absolute inset-x-0 bottom-0 h-1/2 bg-[radial-gradient(ellipse_at_center_bottom,rgba(255,61,61,0.18),rgba(255,107,61,0.06)_45%,transparent_75%)]" />
      {/* Thin bottom heat-band — softens the seam where the section
          ends, without pushing red back to overpowering levels. */}
      <div className="absolute inset-x-0 bottom-0 h-1/4 bg-[linear-gradient(to_top,rgba(255,61,61,0.12),transparent)]" />
    </div>
  );
}

interface BurnQueryProps {
  query: ReturnType<typeof useBurnStats>;
}

/**
 * Headline lockup. The number is framed by four amber HUD corner-ticks
 * so the dead-zero state still reads as a scoreboard rather than empty
 * space — the brackets scale with the digits, so a multi-digit live
 * burn (`1,234,567.89`) still sits cleanly inside the same frame. The
 * phrase below is set as a locked stamp, not a sentence: "TAKEOVER" in
 * cream, "burnt" in red-light, tight tracking pulls them into a single
 * mark.
 */
function BurnHeadline({ query }: BurnQueryProps) {
  let burnedDisplay: ReactNode;
  if (query.isLoading) {
    burnedDisplay = (
      <span className="inline-block h-[0.78em] w-[3.5ch] animate-pulse rounded-sm bg-brand-red-light/25 align-middle" />
    );
  } else if (query.isError || !query.data) {
    burnedDisplay = '—';
  } else {
    burnedDisplay = formatTokenAmount(query.data.burnedFormatted);
  }

  return (
    <div className="flex flex-col items-center gap-5 md:gap-7">
      {/* Bracketed number. Padding controls the offset of the corner
          ticks from the digits themselves — bigger padding on desktop
          so the brackets read at hero scale. */}
      <div className="relative px-5 py-4 md:px-10 md:py-6">
        <CornerTicks />
        <h1
          id="burn-headline"
          className={cn(
            'font-marketing leading-none tracking-tight text-brand-red-light',
            '[text-shadow:0_0_60px_rgba(255,61,61,0.55),0_0_140px_rgba(255,61,61,0.25)]',
          )}
        >
          <span className="block text-7xl tabular-nums md:text-9xl lg:text-[10rem]">
            {burnedDisplay}
          </span>
        </h1>
      </div>

      {/* Stamp-style phrase. "TAKEOVER" stays as-authored (uppercase)
          while "burnt" stays lowercase so the verb doesn't compete
          with the seal — no `uppercase` on the parent. */}
      <p className="font-marketing text-2xl leading-none tracking-[0.04em] text-amber-50 md:text-4xl lg:text-5xl">
        TAKEOVER <span className="text-brand-red-light">burnt</span>
      </p>
    </div>
  );
}

/**
 * Four absolutely-positioned amber corner ticks. Echoes the lobby
 * chrome's `CornerBrackets` so the burn page feels native to the rest
 * of the HUD, but kept inline here to avoid pulling lobby chrome into
 * the marketing surface.
 */
function CornerTicks() {
  const base =
    'absolute h-4 w-4 border-amber-400/80 md:h-8 md:w-8';
  return (
    <>
      <span
        aria-hidden
        className={cn(base, 'left-0 top-0 border-l border-t md:border-l-2 md:border-t-2')}
      />
      <span
        aria-hidden
        className={cn(base, 'right-0 top-0 border-r border-t md:border-r-2 md:border-t-2')}
      />
      <span
        aria-hidden
        className={cn(base, 'bottom-0 left-0 border-b border-l md:border-b-2 md:border-l-2')}
      />
      <span
        aria-hidden
        className={cn(base, 'bottom-0 right-0 border-b border-r md:border-b-2 md:border-r-2')}
      />
    </>
  );
}

/**
 * HUD-style supply share readout: hairline rules flanking a stacked
 * label + tabular value. Reads as a stat panel, not a sentence — the
 * label lives above the value so the eye lands on the percentage.
 */
function PercentOfSupply({ query }: BurnQueryProps) {
  let value: ReactNode;
  if (query.isLoading) {
    value = (
      <span className="inline-block h-5 w-24 animate-pulse rounded bg-amber-500/15 align-middle md:h-7 md:w-28" />
    );
  } else if (query.isError || !query.data) {
    value = '—';
  } else {
    value = formatPercentage(query.data.percentageBps);
  }

  return (
    <div className="mt-2 flex items-center gap-4 md:mt-4 md:gap-6">
      <span aria-hidden className="h-px w-12 bg-gradient-to-r from-transparent to-amber-500/45 md:w-20" />
      <div className="flex flex-col items-center gap-1">
        <span className="font-mono text-[9px] uppercase tracking-[0.32em] text-amber-400/70 md:text-[10px]">
          Total supply burnt
        </span>
        <span className="font-mono text-xl tabular-nums text-amber-100 md:text-2xl">
          {value}
        </span>
      </div>
      <span aria-hidden className="h-px w-12 bg-gradient-to-l from-transparent to-amber-500/45 md:w-20" />
    </div>
  );
}

/**
 * Pre-launch "burn unlocks in…" view. Shown until the treasury balance
 * plus the committed baseline reaches the cap; reuses the same scene as
 * the burn counter, swapping the foreground for a countdown + progress
 * bar.
 *
 * The headline is the *remaining* $TAKEOVER until unlock (counts down to
 * zero as the treasury fills); the bar fills toward 100% in lock-step.
 * Both fall back to a skeleton while the query loads and to `—` on error.
 */
function BurnUnlockProgress({ query }: BurnQueryProps) {
  const unlock = query.data?.unlock;
  const pending = query.isLoading;
  const failed = query.isError || !unlock;

  let remainingDisplay: ReactNode;
  if (pending) {
    remainingDisplay = (
      <span className="inline-block h-[0.78em] w-[4ch] animate-pulse rounded-sm bg-brand-red-light/25 align-middle" />
    );
  } else if (failed) {
    remainingDisplay = '—';
  } else {
    remainingDisplay = formatCompactTokens(unlock.remainingTokens);
  }

  const fillPct = !pending && !failed ? unlock.progressBps / 100 : 0;

  return (
    <div className="flex w-full flex-col items-center gap-6 md:gap-8">
      <span className="font-mono text-[10px] uppercase tracking-[0.34em] text-amber-400/70 md:text-xs">
        Burn unlocks in
      </span>

      {/* Bracketed remaining-amount headline — same HUD lockup as the
          live burn counter so the two states feel like one scoreboard. */}
      <div className="relative px-5 py-4 md:px-10 md:py-6">
        <CornerTicks />
        <h1
          id="burn-unlock-headline"
          className={cn(
            'font-marketing leading-none tracking-tight text-brand-red-light',
            '[text-shadow:0_0_60px_rgba(255,61,61,0.55),0_0_140px_rgba(255,61,61,0.25)]',
          )}
        >
          <span className="block text-6xl tabular-nums md:text-8xl lg:text-[9rem]">
            {remainingDisplay}
          </span>
        </h1>
      </div>

      <p className="font-marketing text-2xl leading-none tracking-[0.04em] text-amber-50 md:text-4xl lg:text-5xl">
        TAKEOVER
      </p>

      {/* Progress bar — fills toward the cap as the treasury accumulates. */}
      <div className="mt-2 w-full max-w-xl md:mt-4">
        <div
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(fillPct)}
          className="relative h-3 w-full rounded-full border border-amber-500/30 bg-black/60 md:h-3.5"
        >
          {/* Filled portion — width animates from 0 on load; the leading
              "nib" rides its right edge so the bar reads as breaking
              forwards. */}
          <div
            className="relative h-full rounded-full bg-gradient-to-r from-amber-500 via-orange-500 to-brand-red-light transition-[width] duration-700 ease-out [box-shadow:0_0_24px_rgba(255,61,61,0.45)]"
            style={{ width: `${fillPct}%` }}
          >
            {/* Everything sits inside a clip matched to the fill's rounded
                shape, so the glisten + nib stay inside the bar and ride
                the load-in width animation with the leading edge. */}
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0 overflow-hidden rounded-full"
            >
              {/* glisten sheen sweeping toward the leading edge */}
              <span className="absolute inset-y-0 left-0 w-1/4 bg-gradient-to-r from-transparent via-white/50 to-transparent animate-burn-glisten motion-reduce:hidden" />
              {/* leading-edge nib: a hot spark filling the tip that blends
                  right→left back into the bar, selling a sense of the fill
                  breaking through forwards. */}
              {!pending && !failed && fillPct > 1 && (
                <>
                  <span className="absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-amber-50 via-amber-200/55 to-transparent animate-burn-nib motion-reduce:opacity-90" />
                  <span className="absolute inset-y-0 right-0 w-[3px] bg-amber-50 [box-shadow:0_0_8px_2px_rgba(255,236,170,0.9)]" />
                </>
              )}
            </span>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.18em] text-amber-100/60 md:text-[11px]">
          <span>{pending || failed ? '—' : formatCompactTokens(unlock.progressTokens)}</span>
          <span>{pending || failed ? '' : `${formatCompactTokens(unlock.capTokens)} cap`}</span>
        </div>
        <p className="mt-4 font-mono text-[11px] leading-relaxed tracking-[0.04em] text-amber-100/45 md:text-xs">
          When buybacks hit 30B tokens (30% of supply) all future buys are burnt.
        </p>
      </div>
    </div>
  );
}

/**
 * Compact whole-token formatter for the billion-scale unlock figures
 * (`2,519,856,802` → `2.52B`). Trailing zeros are trimmed so the cap
 * reads `30B`, not `30.00B`. Sub-thousand values fall back to plain
 * thousands separators.
 */
function formatCompactTokens(n: number): string {
  if (!Number.isFinite(n)) return '—';
  const trim = (s: string) => s.replace(/\.?0+$/, '');
  if (n >= 1e9) return `${trim((n / 1e9).toFixed(2))}B`;
  if (n >= 1e6) return `${trim((n / 1e6).toFixed(2))}M`;
  if (n >= 1e3) return `${trim((n / 1e3).toFixed(1))}K`;
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n);
}

/** Hard ceiling on fractional digits for the headline burn number —
 *  anything beyond reads as visual noise at hero scale. */
const BURN_AMOUNT_MAX_DECIMALS = 2;

/**
 * `burnedFormatted` arrives as a raw `formatUnits` string (e.g. `"0"`,
 * `"1234.567890123456"`). For display we want thousands separators and
 * a fixed precision capped at `BURN_AMOUNT_MAX_DECIMALS` — no matter
 * how many decimals the on-chain value carries, the headline never
 * shows more than that.
 */
function formatTokenAmount(formatted: string): string {
  const n = Number(formatted);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: BURN_AMOUNT_MAX_DECIMALS,
    minimumFractionDigits: 0,
  }).format(n);
}

/**
 * Renders `percentageBps` (basis points, 0..10000) as a percentage with
 * four decimals. Four digits lets a microscopic 0.0001% burn still
 * register visually while a major burn still reads cleanly.
 */
function formatPercentage(bps: number): string {
  const pct = bps / 100;
  return `${pct.toFixed(4)}%`;
}
